import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AiVisibilityLanding from './AiVisibilityLanding';
import { hasAiVisibility } from '../config/aiVisibilityBrands';
import { useAuth } from '../context/AuthContext';
import { Loader2, ExternalLink, ChevronRight, CalendarDays, Layers, Globe, Target, Swords, Grid3x3, Info } from 'lucide-react';
import {
  API, PLATFORM_LABEL, PLATFORM_ORDER, PlatformLogo, CATEGORY_LABEL, SOURCE_LABEL,
  Card, Seg, PeriodPicker, presetRanges, rangeLabel, pct,
} from './YatasAiVisibility';

/**
 * AI Visibility · Kaynak × Marka — yapay zeka hangi siteyi kaynak gösterdiğinde
 * hangi markayı önerdi. Odak marka (varsayılan panelde seçili marka, ör. Yataş
 * Bedding) için iki liste: onu önerirken dayanılan siteler, ve rakipler önerilip
 * odak marka önerilmediğinde dayanılan siteler. Altta site × marka tablosu.
 * Backend: GET /api/ai-visibility/source-brands. Bağ cevap seviyesinde
 * (site o cevapta kaynak + marka o cevapta önerildi) — birliktelik, nedensellik değil.
 */

const BRAND_SITE_TYPES = new Set(['brand', 'brand_other']);
const shortUrl = (u) => { try { const x = new URL(u); return (x.pathname + x.search).replace(/\/$/, '') || '/'; } catch { return u; } };

function TypeBadge({ d }) {
  return (
    <span className="text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
      {d.owner ? `${d.owner} sitesi` : (SOURCE_LABEL[d.type] || d.type)}
    </span>
  );
}

// Bir site satırı: tıklayınca o sitenin kaynak gösterilen sayfaları açılır
function DomainRow({ d, value, max, sub, chips, focusName, barTone }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-gray-50/60 rounded-lg px-1">
        <ChevronRight className={`w-4 h-4 text-gray-400 shrink-0 transition ${open ? 'rotate-90' : ''}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-800 truncate">{d.domain}</span>
            <TypeBadge d={d} />
            <span className="inline-flex gap-1">{d.platforms.map((p) => <PlatformLogo key={p} p={p} size="w-3.5 h-3.5" />)}</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full rounded-full ${barTone}`} style={{ width: `${(value / max) * 100}%` }} />
          </div>
          {chips?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {chips.map((c) => (
                <span key={c.id} className="text-[11px] bg-rose-50 text-rose-700 rounded-full px-2 py-0.5">{c.name} · {c.n}</span>
              ))}
            </div>
          )}
        </div>
        <div className="text-right shrink-0 w-24">
          <div className="text-sm font-semibold text-gray-800">{value} cevap</div>
          {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
        </div>
      </button>
      {open && (
        <div className="pl-8 pr-1 pb-3 space-y-1.5">
          {d.urls.length === 0 && <div className="text-xs text-gray-400">Sayfa adresi yok (yalnız alan adı kaydedildi).</div>}
          {d.urls.map((u) => (
            <a key={u.url} href={u.url} target="_blank" rel="noreferrer"
              className="flex items-start gap-2 text-xs rounded-lg px-2 py-1.5 hover:bg-blue-50/60 group">
              <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-gray-800 truncate">{u.title || shortUrl(u.url)}</span>
                <span className="block text-gray-400 truncate">{shortUrl(u.url)}</span>
              </span>
              <span className="shrink-0 text-gray-500 whitespace-nowrap">
                {u.responses} cevap · <span className="text-blue-700">{focusName} {u.focus}</span> · <span className="text-rose-700">rakip {u.comp}</span>
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function heat(v) {
  if (!v) return { background: 'transparent', color: '#d1d5db' };
  const steps = ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb'];
  const i = Math.min(steps.length - 1, Math.floor(v * steps.length));
  return { background: steps[i], color: i >= 5 ? '#ffffff' : '#1f2937' };
}

export default function AiVisibilitySources() {
  const { selectedBrand, user } = useAuth();
  const brandId = selectedBrand?.id;
  const allowed = hasAiVisibility(brandId, user?.email);
  const defaultPeriod = () => { const p = presetRanges()[3]; return { from: p.from, to: p.to }; }; // Bu ay
  const [period, setPeriod] = useState(defaultPeriod);
  const [platform, setPlatform] = useState(null);
  const [category, setCategory] = useState(null);
  const [world, setWorld] = useState(null);
  const [focus, setFocus] = useState(null);
  const [showBrandSites, setShowBrandSites] = useState(false);
  const [showAll, setShowAll] = useState({ focus: false, gap: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      Object.entries({ from: period?.from, to: period?.to, platform, category, world, focus })
        .forEach(([k, v]) => { if (v != null && v !== '') p.set(k, v); });
      const res = await fetch(`${API}/api/ai-visibility/source-brands?${p}`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setData(j);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [allowed, brandId, period, platform, category, world, focus]);

  useEffect(() => { setPeriod(defaultPeriod()); setCategory(null); setWorld(null); setFocus(null); }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const brands = data?.brands || [];
  const nameOf = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const focusId = data?.focusId;
  const focusName = nameOf[focusId] || '';
  const domains = useMemo(
    () => (data?.domains || []).filter((d) => showBrandSites || !BRAND_SITE_TYPES.has(d.type)),
    [data, showBrandSites],
  );
  const t = data?.totals;
  const avgFocus = t?.withRec ? t.focusResp / t.withRec : null;

  const focusList = useMemo(() => domains.filter((d) => d.focus > 0).sort((a, b) => b.focus - a.focus || b.responses - a.responses), [domains]);
  const gapList = useMemo(() => domains.filter((d) => d.compOnly > 0).sort((a, b) => b.compOnly - a.compOnly || b.comp - a.comp), [domains]);
  const compChips = (d) => Object.entries(d.byBrand).map(([id, n]) => ({ id: Number(id), n, name: nameOf[id] }))
    .filter((c) => c.id !== focusId && c.name).sort((a, b) => b.n - a.n).slice(0, 4);

  // Tablo: kendi markalar + bu sitelerde en çok önerilen 10 rakip
  const matrixRows = domains.slice(0, 30);
  const matrixCols = useMemo(() => {
    const tot = {};
    for (const d of domains) for (const [id, n] of Object.entries(d.byBrand)) tot[id] = (tot[id] || 0) + n;
    const own = brands.filter((b) => (data?.ownIds || []).includes(b.id));
    const comp = brands.filter((b) => !(data?.ownIds || []).includes(b.id) && tot[b.id]).sort((a, b) => tot[b.id] - tot[a.id]).slice(0, 10);
    return [...own, ...comp];
  }, [domains, brands, data]);

  const availablePlatforms = useMemo(() => {
    const set = new Set();
    for (const r of data?.runs || []) for (const e of r.config?.platforms || []) set.add(e.platform);
    return PLATFORM_ORDER.filter((p) => set.has(p));
  }, [data]);

  if (!allowed) return <AiVisibilityLanding />;

  const sel = data?.selection;
  const own = brands.filter((b) => b.kind === 'own');
  const comps = brands.filter((b) => b.kind !== 'own');
  const list = (key, rows) => (showAll[key] ? rows : rows.slice(0, 15));

  return (
    <div className="min-h-screen" lang="tr">
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI Visibility · Kaynak × Marka</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Yapay zeka bir markayı önerirken hangi siteleri kaynak gösterdi? Bir siteyi aç, yapay zekanın okuduğu sayfaları gör.
          </p>
        </div>

        {/* Filtreler */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm px-4 py-3 flex flex-wrap items-center gap-2">
          {(data?.worlds || []).length > 0 && (
            <>
              <div className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 pl-3 pr-1 py-1">
                <Layers className="w-4 h-4 text-blue-600" />
                <select value={data.world || ''} onChange={(e) => { setWorld(e.target.value); setCategory(null); setFocus(null); }}
                  className="text-sm font-semibold text-blue-800 bg-transparent border-0 focus:ring-0 pr-8 py-1">
                  {data.worlds.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
                </select>
              </div>
              <select value={category || ''} onChange={(e) => setCategory(e.target.value || null)}
                className="text-sm border border-gray-200/80 rounded-xl pl-3 pr-8 py-2 bg-white shadow-sm font-medium text-gray-700">
                <option value="">Tüm kategoriler</option>
                {(data?.categories || []).map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
              </select>
              <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
            </>
          )}
          <CalendarDays className="w-4 h-4 text-gray-400" />
          <PeriodPicker value={period?.from ? period : (sel?.from ? { from: sel.from, to: sel.to } : null)} onChange={(v) => setPeriod(v)} />
          <div className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
          <Seg value={platform} onChange={setPlatform}
            options={[{ value: null, label: 'Tümü' }, ...availablePlatforms.map((p) => ({ value: p, label: PLATFORM_LABEL[p], logo: p }))]} />
          {loading && data && <Loader2 className="animate-spin text-gray-400 ml-auto" size={16} />}
        </div>

        {/* Odak marka */}
        {brands.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 mr-1">Odak marka:</span>
            {own.map((b) => (
              <button key={b.id} onClick={() => setFocus(b.id)}
                className={`text-sm font-medium px-3 py-1.5 rounded-xl border shadow-sm ${b.id === focusId ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200/80 text-gray-700 hover:bg-gray-50'}`}>
                {b.name}
              </button>
            ))}
            <select value={comps.some((b) => b.id === focusId) ? focusId : ''} onChange={(e) => e.target.value && setFocus(Number(e.target.value))}
              className={`text-sm border rounded-xl pl-3 pr-8 py-1.5 shadow-sm font-medium ${comps.some((b) => b.id === focusId) ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200/80 bg-white text-gray-700'}`}>
              <option value="">Rakip seç…</option>
              {comps.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <label className="ml-auto inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={showBrandSites} onChange={(e) => setShowBrandSites(e.target.checked)} className="rounded border-gray-300" />
              Markaların kendi sitelerini de göster
            </label>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl p-3">{error}</div>}
        {loading && !data && (
          <div className="flex justify-center items-center h-[240px]">
            <div className="loading-logo-container"><img src="/logoicon.png" alt="Glowytics" className="loading-logo" /></div>
          </div>
        )}
        {data?.empty && <div className="bg-white rounded-3xl border border-gray-200/80 p-8 text-center text-sm text-gray-500">Bu dönemde ölçüm yok.</div>}

        {data && !data.empty && t && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5">
                <div className="text-sm text-gray-500">Kaynak gösterilen cevaplar</div>
                <div className="text-2xl font-semibold text-gray-900 mt-1">{t.cited} <span className="text-base font-normal text-gray-400">/ {t.responses}</span></div>
                <div className="text-xs text-gray-400 mt-1">{rangeLabel(sel)}</div>
              </div>
              <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5">
                <div className="text-sm text-gray-500">{focusName} önerilen cevaplar</div>
                <div className="text-2xl font-semibold text-blue-700 mt-1">{t.focusResp}</div>
                <div className="text-xs text-gray-400 mt-1">takip edilen bir markanın önerildiği {t.withRec} cevabın {pct(avgFocus)}'i</div>
              </div>
              <div className="bg-white rounded-3xl border border-gray-200/80 shadow-sm p-5">
                <div className="text-sm text-gray-500">Rakip önerilip {focusName} önerilmeyen</div>
                <div className="text-2xl font-semibold text-rose-700 mt-1">{t.compOnlyResp}</div>
                <div className="text-xs text-gray-400 mt-1">cevap — aşağıdaki sağ liste bunların kaynakları</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card icon={Target} tone="blue" title={`${focusName} önerilirken kaynak gösterilen siteler`}
                sub={`Sağda: bu siteye dayanan cevaplarda ${focusName}'in önerilme oranı (ortalama ${pct(avgFocus)})`}>
                {focusList.length === 0 && <div className="text-sm text-gray-400">Bu seçimde yok.</div>}
                {list('focus', focusList).map((d) => (
                  <DomainRow key={d.domain} d={d} value={d.focus} max={focusList[0]?.focus || 1} focusName={focusName} barTone="bg-blue-500"
                    sub={`${pct(d.withRec ? d.focus / d.withRec : null)} önerildi`} />
                ))}
                {focusList.length > 15 && (
                  <button onClick={() => setShowAll((s) => ({ ...s, focus: !s.focus }))} className="mt-3 text-sm font-medium text-blue-600 hover:underline">
                    {showAll.focus ? 'Daha az göster' : `Tümünü göster (${focusList.length})`}
                  </button>
                )}
              </Card>

              <Card icon={Swords} tone="rose" title={`Rakipler önerilip ${focusName} önerilmediğinde kaynak gösterilen siteler`}
                sub="Etiketler: o sitenin geçtiği cevaplarda en çok önerilen rakipler">
                {gapList.length === 0 && <div className="text-sm text-gray-400">Bu seçimde yok.</div>}
                {list('gap', gapList).map((d) => (
                  <DomainRow key={d.domain} d={d} value={d.compOnly} max={gapList[0]?.compOnly || 1} focusName={focusName} barTone="bg-rose-400"
                    chips={compChips(d)} sub={`${d.focus} cevapta ${focusName} de var`} />
                ))}
                {gapList.length > 15 && (
                  <button onClick={() => setShowAll((s) => ({ ...s, gap: !s.gap }))} className="mt-3 text-sm font-medium text-blue-600 hover:underline">
                    {showAll.gap ? 'Daha az göster' : `Tümünü göster (${gapList.length})`}
                  </button>
                )}
              </Card>
            </div>

            <Card icon={Grid3x3} tone="purple" title="Site × marka"
              sub="En çok kaynak gösterilen 30 site. Hücre: o site kaynak gösterildiğinde markanın önerildiği cevap sayısı; renk, sitenin cevapları içindeki payı.">
              <div className="overflow-x-auto -mx-2">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-gray-600 sticky left-0 bg-white">Site</th>
                      <th className="py-2 px-2 text-xs font-semibold text-gray-600 text-right">Cevap</th>
                      {matrixCols.map((b) => (
                        <th key={b.id} className={`py-2 px-2 text-xs font-semibold text-center whitespace-nowrap ${b.id === focusId ? 'text-blue-700' : 'text-gray-600'}`}>{b.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixRows.map((d) => (
                      <tr key={d.domain} className="border-t border-gray-100">
                        <td className="py-1.5 px-2 sticky left-0 bg-white whitespace-nowrap">
                          <span className="font-medium text-gray-800">{d.domain}</span>
                          <span className="ml-2 text-[11px] text-gray-400">{d.owner ? `${d.owner} sitesi` : (SOURCE_LABEL[d.type] || d.type)}</span>
                        </td>
                        <td className="py-1.5 px-2 text-right text-gray-500">{d.withRec}</td>
                        {matrixCols.map((b) => {
                          const n = d.byBrand[b.id] || 0;
                          return (
                            <td key={b.id} className="py-1.5 px-1 text-center">
                              <span className="inline-block min-w-[2.25rem] rounded-md px-1.5 py-0.5 text-xs font-medium" style={heat(d.withRec ? n / d.withRec : 0)}>{n || '·'}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="flex items-start gap-2 text-xs text-gray-500 max-w-4xl">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Bağ cevap seviyesinde kuruluyor: bir site o cevapta kaynak gösterildiyse ve marka o cevapta önerildiyse sayılıyor. Yani site markayı
                bizzat önermiş olmayabilir, yapay zeka aynı cevapta ona da dayanmış. Birliktelik gösterir, nedensellik değil. Bir cevap birden fazla
                siteyi kaynak gösterebildiği için sayılar toplanmaz. Varsayılan görünümde markaların kendi siteleri gizli (yukarıdaki kutuyla açılır).
                {' '}<Globe className="inline w-3 h-3" /> Marka adı geçen sorular hesaba katılmıyor.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
